import React, { useState, useEffect } from 'react';
import {
  EditorState,
  convertFromRaw,
  convertToRaw
} from '@ibinder/ui-common-richeditor';
import { draftjsToMd, mdToDraftjs } from 'draftjs-md-converter';
import { TextElement } from '@ibinder/ui-common-core';

import {
  FormFieldText,
  Select,
  SelectContainer,
  Col
} from './GeneralSettings.styles';
import { selectData } from './GeneralSettings.constants';
import { FormFiledType, GeneralSettingsProps } from './GeneralSettings.types';
import { GeneralSettingsView } from './GeneralSettings.view';
import { PrintProviderType } from 'src/components/PrintProviders/types/common.types';

export const GeneralSettings: React.FC<GeneralSettingsProps> = ({
  data,
  updateData,
  reqData
}) => {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const updateDescription = (newEditorState: EditorState) => {
    setEditorState(newEditorState);
    updateData(
      'decription',
      draftjsToMd(convertToRaw(editorState.getCurrentContent()))
    );
  };

  const updateOMSType = (type: PrintProviderType['omsType']): void => {
    if (type === 'Custom') {
      updateData('emailForOrders', reqData?.emailForOrders);
    }
    if (type === 'Standard') {
      updateData('omsServiceUrl', reqData?.omsServiceUrl);
      updateData('omsSecretKey', reqData?.omsSecretKey);
    }
    updateData('omsType', type);
  };

  const updateOrderingSystem = (
    checked: boolean,
    type: 'Byggnet' | 'iBinder'
  ) => {
    if (checked) {
      updateData('orderingSystems', [
        ...(data?.orderingSystems ?? []),
        {
          id: type
        }
      ]);
      return;
    }
    updateData(
      'orderingSystems',
      data?.orderingSystems?.filter(({ id }) => id !== type)
    );
  };

  const renderFormField = ({
    id,
    label,
    required,
    width,
    type
  }: FormFiledType) => {
    if (type === 'select') {
      const activeIndex = selectData[id].findIndex(
        ({ Key }) => Key === data[id]
      );
      return (
        <SelectContainer>
          <TextElement fontStyle="smallFont" color="discrete70">
            {label}
          </TextElement>
          <Select
            dropdownData={selectData[id]}
            withSortButton={false}
            activeIndex={activeIndex === -1 ? 0 : activeIndex}
            onClickSelectItem={(idx) => updateData(id, selectData[id][idx].Key)}
          />
        </SelectContainer>
      );
    }

    return (
      <FormFieldText
        name={label}
        label={label}
        value={data[id] ?? ''}
        required={required}
        width={width}
        changeValue={(e) => updateData(id, e.target.value)}
      />
    );
  };

  const renderOMSFields = () => {
    if (data?.omsType === 'Standard') {
      return (
        <FormFieldText
          name="E-mail for orders"
          label="E-mail for orders"
          value={data?.emailForOrders ?? ''}
          required={true}
          width="25%"
          changeValue={(e) => updateData('emailForOrders', e.target.value)}
        />
      );
    }
    return (
      <Col gap={0.5}>
        <FormFieldText
          name="OMS service url"
          label="OMS service url"
          value={data?.omsServiceUrl ?? ''}
          required={true}
          width="25%"
          changeValue={(e) => updateData('omsServiceUrl', e.target.value)}
        />
        <FormFieldText
          type="password"
          name="OMS Secret key"
          label="OMS Secret key"
          value={data?.omsSecretKey ?? ''}
          required={true}
          width="25%"
          changeValue={(e) => updateData('omsSecretKey', e.target.value)}
        />
      </Col>
    );
  };

  useEffect(() => {
    if (reqData?.decription) {
      setEditorState(
        EditorState.createWithContent(
          convertFromRaw(mdToDraftjs(reqData?.decription))
        )
      );
    }
  }, [reqData]);

  return (
    <GeneralSettingsView
      data={data}
      updateData={updateData}
      editorState={editorState}
      updateDescription={updateDescription}
      updateOrderingSystem={updateOrderingSystem}
      renderFormField={renderFormField}
      updateOMSType={updateOMSType}
      renderOMSFields={renderOMSFields}
    />
  );
};
